import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getOrganizationTypeById, deleteOrganizationType, getOrganizationTypes, getOrgTypeServiceTypes, getOrgTypeTopicTypes } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Tag,
  Edit3,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Building2,
  ChevronUp,
  ChevronDown,
  Grid3x3,
  Layers,
  BookOpen
} from 'lucide-react';
import OrgTypeSectionsTab from './OrgTypeSectionsTab';
import ServiceTypesReadOnlyView from '../components/ServiceTypesReadOnlyView';
import TopicTypesReadOnlyView from '../components/TopicTypesReadOnlyView';
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';

export default function ViewOrganizationTypePage() {
  const { organizationTypeId } = useParams<{ organizationTypeId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteOrganizationTypeFn = useAction(deleteOrganizationType);
  const [activeTab, setActiveTab] = useState<'details' | 'sections' | 'services' | 'topics'>('details');

  // Fetch organization type data
  const {
    data: organizationType,
    isLoading,
    error
  } = useQuery(getOrganizationTypeById, { id: organizationTypeId! });

  // Fetch all organization types for navigation
  const { data: allOrganizationTypes = [] } = useQuery(getOrganizationTypes);

  // Fetch linked service types and topic types (read-only)
  const { data: orgTypeServiceTypes = [] } = useQuery(getOrgTypeServiceTypes, { organizationTypeId: organizationTypeId! });
  const { data: orgTypeTopicTypes = [] } = useQuery(getOrgTypeTopicTypes, { organizationTypeId: organizationTypeId! });

  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    navigate('/admin/organization-types');
  };

  // Find current index and get prev/next organization types
  const currentIndex = (allOrganizationTypes as any[]).findIndex((type: any) => type.id === organizationTypeId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (allOrganizationTypes as any[]).length - 1;
  const prevOrganizationType = hasPrev ? (allOrganizationTypes as any[])[currentIndex - 1] : null;
  const nextOrganizationType = hasNext ? (allOrganizationTypes as any[])[currentIndex + 1] : null;

  const handleDelete = async () => {
    if (!organizationType) return;
    if (!confirm(`Are you sure you want to delete "${organizationType.label}"?`)) return;

    try {
      await deleteOrganizationTypeFn({ id: organizationType.id });
      toast({
        title: "Success",
        description: "Organization type deleted successfully"
      });
      navigate('/admin/organization-types');
    } catch (err: any) {
      console.error('Error deleting organization type:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete organization type",
        variant: "destructive"
      });
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to view organization types.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization type...</p>
        </div>
      </div>
    );
  }

  if (error || !organizationType) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Tag className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Organization Type Not Found</h3>
          <p className="text-gray-600 mb-4">The organization type you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Organization Types
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <div className="space-y-3">
          {/* Navigation Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => prevOrganizationType && navigate(`/admin/organization-types/${prevOrganizationType.id}`)}
              disabled={!hasPrev}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasPrev ? `Previous: ${prevOrganizationType?.label}` : 'No previous item'}
            >
              <ChevronUp className="w-4 h-4" />
              <span className="text-sm font-medium">Up</span>
            </button>
            <button
              onClick={() => nextOrganizationType && navigate(`/admin/organization-types/${nextOrganizationType.id}`)}
              disabled={!hasNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasNext ? `Next: ${nextOrganizationType?.label}` : 'No next item'}
            >
              <span className="text-sm font-medium">Down</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Action Buttons */}
          <ActionButtons>
            <ActionButtonLink
              to={`/admin/organization-types/${organizationType.id}/edit`}
            >
              Edit
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
            >
              Delete
            </ActionButtonDanger>
          </ActionButtons>
        </div>
      </SidebarSection>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Key" value={organizationType.key} />
          <DetailField label="Status" value={organizationType.isActive ? 'Active' : 'Inactive'} />
          <DetailField label="Created" value={formatDate(organizationType.createdAt)} />
          <DetailField label="Last Updated" value={formatDate(organizationType.updatedAt)} />
          <DetailField label="Organizations" value={organizationType._count?.organizations || 0} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={[
        { label: 'Admin', href: '/admin' },
        { label: 'Organization Types', href: '/admin/organization-types', isClickable: true }
      ]}
      currentPageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
      authUser={user}
    >
      {/* Organization Type Header */}
      <div className="mb-8">
        {/* Title and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{organizationType.label}</h1>
            </div>

            <p className="text-lg text-gray-600 font-mono mb-4">{organizationType.key}</p>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 ml-4">
            {organizationType.isActive ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-100 text-red-800">
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Inactive</span>
              </div>
            )}
          </div>
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Created {formatDate(organizationType.createdAt)}</span>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Updated {formatDate(organizationType.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Tag className="w-4 h-4" />
          Details
        </button>
        <button
          onClick={() => setActiveTab('sections')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sections'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Grid3x3 className="w-4 h-4" />
          Sections
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Layers className="w-4 h-4" />
          Services
        </button>
        <button
          onClick={() => setActiveTab('topics')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'topics'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Topics
        </button>
      </div>

      {/* Sections Tab (read-only) */}
      {activeTab === 'sections' && (
        <OrgTypeSectionsTab organizationTypeId={organizationTypeId!} readOnly />
      )}

      {/* Services Tab (read-only) */}
      {activeTab === 'services' && (
        <ServiceTypesReadOnlyView linkedServiceTypes={orgTypeServiceTypes as any[]} />
      )}

      {/* Topics Tab (read-only) */}
      {activeTab === 'topics' && (
        <TopicTypesReadOnlyView linkedTopicTypes={orgTypeTopicTypes as any[]} />
      )}

      {/* Organization Type Content */}
      {activeTab === 'details' && <div className="space-y-6">
        {/* Type Badge */}
        <div className="flex flex-wrap gap-3 mb-6">
          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50 border-blue-200 text-blue-700">
            <Tag className="w-5 h-5" />
            <span className="font-medium">{organizationType.key}</span>
          </span>
        </div>

        {/* Description */}
        {organizationType.description && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
            <div className="prose prose-gray max-w-none">
              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {organizationType.description}
              </p>
            </div>
          </div>
        )}

        {/* Quick Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Key</p>
                <p className="font-medium text-gray-900 font-mono">{organizationType.key}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Organizations Using This Type</p>
                <p className="font-medium text-gray-900">{organizationType._count?.organizations || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Organizations Using This Type */}
        {organizationType.organizations && organizationType.organizations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Organizations Using This Type
            </h3>
            <div className="space-y-2">
              {organizationType.organizations.map((org: any) => (
                <Link
                  key={org.id}
                  to={`/admin/organizations/${org.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{org.name}</p>
                    <p className="text-sm text-gray-500">{org.slug}</p>
                  </div>
                  {org.isActive ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Active</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Inactive</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Additional Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Info</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status</span>
              <span className="font-medium text-gray-900">{organizationType.isActive ? 'Active' : 'Inactive'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated</span>
              <span className="font-medium text-gray-900">{formatDate(organizationType.updatedAt)}</span>
            </div>
          </div>
        </div>
      </div>}
    </IndividualViewPageLayout>
  );
}



